import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Thermometer, CheckSquare, AlertTriangle, Package, CalendarIcon, RefreshCw, Clock, Camera, Check } from 'lucide-react';
import { useAgent } from '../App';
import { toast } from 'sonner';
import SyncStatus from '../components/SyncStatus';
import DeleteDraftButton from '../components/DeleteDraftButton';
import { labelConflicts } from '../lib/draftMerge.mjs';
import { newerDraftUi } from '../lib/draftResume.mjs';
import { adoptServerDraft, clearLocalAfterSubmit, discardDraft, loadBestDraft, patchDraftUi, reconcileTransferredDraft, rememberDraft, resolveConflict, subscribeSync } from '../lib/syncEngine';
import { ownershipFromConflict } from '../lib/draftTransfer.mjs';
import { compressPhoto, PHOTO_MAX_COUNT } from '../lib/photoCompress.mjs';
import { clearAgentPhotos, deleteLocalDraft, deleteReportPhoto, listReportPhotos, queueReportPhoto, readLocalDraft, tagReportPhoto, writeLocalDraft } from '../lib/offlineStore';
import { photosForReport, submitKeepsLocalDraft, transferPhotoDecision } from '../lib/reportPhotoQueue.mjs';
import { toggleControlOption, unmatchedControlParts } from '../lib/controlItem.mjs';
import { sentPhotoActions } from '../lib/sentPhoto.mjs';
import { endDateForNight, serviceHoursIssue } from '../lib/serviceHours.mjs';
import { FLOOR_OBSERVATION_OPTIONS } from '../lib/floorObservations.mjs';
import { SECTION_STATUS_LABEL, sectionStatus } from '../lib/sectionProgress.mjs';
import { uploadQueuedPhotos } from '../lib/reportPhotoSync';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { desktopApi, mailSubmitMessage } from '../lib/desktopApi';
import { formatDateTime24 } from '../lib/time';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// La configuration enregistrée dans le panneau admin fait foi, y compris une
// liste vidée. Les valeurs intégrées ne servent qu'en son absence.
const zoneList = (configList, defaults) => (Array.isArray(configList) ? configList : defaults);

const draftUiKey = (agentId) => `vatelsec-draft-ui:${agentId}`;

function readSessionDraftUi(agentId) {
  if (!agentId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(draftUiKey(agentId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    void error;
    return null;
  }
}

function writeSessionDraftUi(agentId, ui) {
  if (!agentId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(draftUiKey(agentId), JSON.stringify(ui));
  } catch (error) {
    /* navigation privée ou quota */
    void error;
  }
}

function clearSessionDraftUi(agentId) {
  if (!agentId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(draftUiKey(agentId));
  } catch (error) {
    void error;
  }
}

function clampStep(step) {
  const value = Number(step);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(REPORT_STEPS.length - 1, Math.round(value)));
}

function foldAppliance(location) {
  return String(location || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function temperaturePolarity(location, index) {
  const name = foldAppliance(location);
  if (name.includes('congel') || name.includes('freezer')) return '−';
  if (index < 2 || name.startsWith('vitrine pat') || name.startsWith('timbre office')) return '+';
  return '−';
}

const REPORT_STEPS = [
  { id: 'general', label: 'Informations' },
  { id: 'ecoles', label: 'Écoles' },
  { id: 'etage2', label: '2ème étage' },
  { id: 'reception', label: 'Réception' },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'cuisine', label: 'Cuisine' },
  { id: 'sous-sols', label: 'Sous-sols' },
  { id: 'temperatures', label: 'Frigos' },
  { id: 'manoeuvres', label: 'Manœuvres' },
  { id: 'controle-exterieurs', label: 'Extérieurs' },
  { id: 'controle-internes', label: 'Étages' },
  { id: 'observations-etages', label: 'Observations' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'remise-materiel', label: 'Remise' },
  { id: 'materiel', label: 'Matériel' },
  { id: 'finaliser', label: 'Enregistrer' },
];

// Porte de secours des écoles : un seul choix, RAS ou une anomalie.
const PORTE_SECOURS_ANOMALIES = ['Porte ouverte', 'Porte non-verrouillée'];
const PORTE_SECOURS_CHOICES = ['RAS', ...PORTE_SECOURS_ANOMALIES];

const nowHHMM = () => {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const NowStamp = ({ onStamp, label = 'Maintenant' }) => (
  <button
    type="button"
    onClick={onStamp}
    className="inline-flex h-12 items-center justify-center gap-1 rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground whitespace-nowrap"
  >
    <Clock className="h-4 w-4" />
    {label}
  </button>
);

// Remarques qui reviennent presque chaque nuit, proposées en un clic.
// Repris du panneau admin dès que le réglage remark_templates est disponible.
const DEFAULT_REMARK_TEMPLATES = {
  'Prise de service': [
    "Reçu de {reception} les clés de la réception + le natel Vatel + la liste d'occupation + 4 cartes de chambre vides.",
    'Reçu de {reception} les clés du Café Vatel + la clé du frigo cuisine.',
    'Reçu de {reception} la liste des clients de la nuit.',
    'Reçu de … 1 enveloppe caisse bar.',
    'Reçu de … 1 enveloppe caisse banquet.',
    'Reçu de … la caisse restaurant.',
    "Matériel et liste d'occupation déposés sur le bureau de sécurité à mon arrivée.",
  ],
  Livraisons: [
    'Livraison des journaux.',
    "Livraison boulangerie, pain déposé à l'office.",
    'Livraison des légumes.',
    'Livraison Transgourmet, marchandise descendue en chambre froide.',
    'Pas de livraison boulangerie.',
    'Pas de livraison des journaux.',
  ],
  'Clients et chambres': [
    'Départ des clients de la chambre … / carte de chambre récupérée.',
    "Arrivée tardive de … / bulletin d'arrivée rempli et encaissement selon procédure.",
    "Chambre … ouverte à la demande du client qui n'avait pas sa carte.",
    'Chambre … ouverte à la demande de l\'étudiant(e) … après vérification.',
    'Toqué à la chambre … pour bruit excessif.',
  ],
  'Contrôles de nuit': [
    'Contrôle des étages 4 et 5 : aucun carton « petit déjeuner » constaté.',
    'Ronde extérieure : RAS.',
    'Salle 006 occupée par la student life / fin à …',
    'Interruption des rondes pendant la manifestation, reprise à …',
    'Odeur de tabac constatée au … étage.',
  ],
  'Fin de service': [
    'Remis à … les clés du Café Vatel + la clé du frigo cuisine.',
    'Remis à … les clés de la réception + le natel Vatel + les cartes de chambre.',
    'Remis à … les enveloppes caisses bar et banquet.',
    'Ouverture de la porte principale Vatel.',
  ],
};

const ReportForm = ({ editMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reportId } = useParams();
  const { currentAgent } = useAgent();
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(editMode);
  const [manoeuvresList, setManoeuvresList] = useState([]);
  const [receptionnistesList, setReceptionnistesList] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [draftStatus, setDraftStatus] = useState('');
  const [draftDecisionMade, setDraftDecisionMade] = useState(editMode);
  const [draftSaving, setDraftSaving] = useState(false);
  const [hoursError, setHoursError] = useState('');
  const [localPhotos, setLocalPhotos] = useState([]);
  const [formTouched, setFormTouched] = useState(false);
  const [transferAgentId, setTransferAgentId] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);
  const [serverPhotos, setServerPhotos] = useState([]);
  const [canWriteReport, setCanWriteReport] = useState(false);
  const [reportConfig, setReportConfig] = useState(null);
  const [remarkTemplates, setRemarkTemplates] = useState(null);
  const [showRemarkTemplates, setShowRemarkTemplates] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const [mobileStep, setMobileStep] = useState(0);
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState([]);
  const [conflictKeys, setConflictKeys] = useState([]);
  const mobileStepRef = useRef(0);
  const openSectionsRef = useRef([]);
  const scrollYRef = useRef(0);
  const restoreScrollRef = useRef(null);
  const ignoreScrollUntilRef = useRef(0);
  const formTouchedRef = useRef(false);
  const draftDecisionMadeRef = useRef(editMode);
  const saveDraftRef = useRef(null);
  const transferLockedRef = useRef(false);
  const abandonLockedRef = useRef(false);

  useEffect(() => { mobileStepRef.current = mobileStep; }, [mobileStep]);
  useEffect(() => { openSectionsRef.current = openSections; }, [openSections]);
  useEffect(() => { formTouchedRef.current = formTouched; }, [formTouched]);
  useEffect(() => { draftDecisionMadeRef.current = draftDecisionMade; }, [draftDecisionMade]);

  const refreshLocalPhotos = useCallback(async () => {
    if (!currentAgent?.id) {
      setLocalPhotos([]);
      return;
    }
    const rows = await listReportPhotos(currentAgent.id).catch(() => []);
    setLocalPhotos(rows.map((row) => ({
      ...row,
      url: row.blob ? URL.createObjectURL(row.blob) : '',
    })));
  }, [currentAgent?.id]);

  useEffect(() => {
    refreshLocalPhotos();
  }, [refreshLocalPhotos]);

  const handleAddReportPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentAgent?.id) return;
    if (localPhotos.length >= PHOTO_MAX_COUNT) {
      toast.error(`${PHOTO_MAX_COUNT} photos maximum pour un rapport`);
      return;
    }
    try {
      const compressed = await compressPhoto(file);
      const reportKey = formData.id;
      if (!reportKey) {
        toast.error('Photo sans rapport');
        return;
      }
      await queueReportPhoto(currentAgent.id, compressed, reportKey);
      await refreshLocalPhotos();
    } catch (error) {
      toast.error('Photo illisible');
    }
  };

  const handleRemoveReportPhoto = async (id) => {
    await deleteReportPhoto(id);
    await refreshLocalPhotos();
  };

  const handleDeleteSentPhoto = async (photo) => {
    const decision = sentPhotoActions(canWriteReport);
    if (!decision.canDelete || !formData.id) return;
    if (!window.confirm(decision.confirm)) return;
    try {
      await desktopApi.deleteReportPhoto(formData.id, photo.id);
      if (photo.url) URL.revokeObjectURL(photo.url);
      setServerPhotos((rows) => rows.filter((row) => row.id !== photo.id));
      toast.success('Photo supprimée');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Impossible de supprimer la photo');
    }
  };

  const captureUi = useCallback(() => ({
    step: mobileStepRef.current,
    scrollY: Math.max(0, Number(scrollYRef.current) || 0),
    openSections: Array.isArray(openSectionsRef.current) ? openSectionsRef.current : [],
    at: Date.now(),
  }), []);

  const applyUi = useCallback((ui) => {
    if (!ui || typeof ui !== 'object') return;
    const step = clampStep(ui.step);
    const y = Math.max(0, Number(ui.scrollY) || 0);
    if (Array.isArray(ui.openSections)) {
      openSectionsRef.current = ui.openSections;
      setOpenSections(ui.openSections);
    }
    restoreScrollRef.current = { y };
    scrollYRef.current = y;
    mobileStepRef.current = step;
    ignoreScrollUntilRef.current = Date.now() + 500;
    setMobileStep(step);
    const restoreScroll = () => {
      scrollYRef.current = y;
      window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    };
    requestAnimationFrame(() => requestAnimationFrame(restoreScroll));
    window.setTimeout(restoreScroll, 80);
    window.setTimeout(restoreScroll, 280);
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !draftDecisionMade) return;
    if (restoreScrollRef.current) {
      restoreScrollRef.current = null;
      return;
    }
    scrollYRef.current = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [mobileStep, isMobile, draftDecisionMade]);

  // Charger les manœuvres et réceptionnistes depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [manRes, recRes, agentsRes, cfgRes, tplRes] = await Promise.all([
          desktopApi.getManoeuvres(),
          desktopApi.getSetting('receptionistes').catch(() => null),
          desktopApi.getAgents().catch(() => []),
          desktopApi.getSetting('report_config').catch(() => null),
          desktopApi.getSetting('remark_templates').catch(() => null),
        ]);
        setManoeuvresList(manRes);
        setAgentsList(agentsRes || []);
        if (cfgRes?.value) {
          try { setReportConfig(JSON.parse(cfgRes.value)); } catch (e) { /* keep null */ }
        }
        if (tplRes?.value) {
          try { setRemarkTemplates(JSON.parse(tplRes.value)); } catch (e) { /* keep null */ }
        }
        // Prise et fin de service restent vides sur un nouveau rapport.
        // Un brouillon ou un rapport déjà ouvert garde les heures qu'il contient.
        if (recRes?.value) {
          try { setReceptionnistesList(JSON.parse(recRes.value)); } catch (e) { /* keep empty */ }
        }
      } catch (error) {
        console.error('Erreur chargement:', error);
      }
    };
    fetchData();
  }, [editMode]);

  // Charger le rapport existant en mode édition
  useEffect(() => {
    if (editMode && reportId) {
      const fetchReport = async () => {
        try {
          setLoadingReport(true);
          const response = await desktopApi.getReportById(reportId);
          setCanWriteReport(Boolean(response.can_write));
          const reportFields = { ...response };
          delete reportFields.can_write;
          // Merge API response with default form structure to ensure all fields exist
          setFormData(prev => ({
            ...prev,
            ...reportFields,
            ecoles_controle: response.ecoles_controle || {},
            ecole_porte_secours: response.ecole_porte_secours || '',
            etage2_controle: response.etage2_controle || {},
            sous_sols_controle: normalizeSousSolsControle(response.sous_sols_controle || prev.sous_sols_controle),
            reception_controle: response.reception_controle || prev.reception_controle,
            restaurant_controle: response.restaurant_controle || prev.restaurant_controle,
            cuisine_controle: response.cuisine_controle || prev.cuisine_controle,
            ecoles_ras: response.ecoles_ras || {},
            etage2_ras: response.etage2_ras || {},
            reception_ras: response.reception_ras || {},
            temperature_checks: response.temperature_checks || prev.temperature_checks,
            manoeuvres: response.manoeuvres || {},
            manoeuvre_times: response.manoeuvre_times || {},
            zone_times: response.zone_times || prev.zone_times,
            started_by_agent_id: response.started_by_agent_id || '',
            started_by_agent_name: response.started_by_agent_name || '',
            transferred_to_agent_id: response.transferred_to_agent_id || '',
            transferred_to_agent_name: response.transferred_to_agent_name || '',
            transfer_time: normalizeTime(response.transfer_time),
            transfer_notes: response.transfer_notes || '',
            controle_exterieurs: response.controle_exterieurs || prev.controle_exterieurs,
            controle_internes_hotel: response.controle_internes_hotel || prev.controle_internes_hotel,
            observations_etages: response.observations_etages || prev.observations_etages,
            incidents: response.incidents || [],
            material_checklist: response.material_checklist || prev.material_checklist
          }));
        } catch (error) {
          toast.error('Erreur lors du chargement du rapport');
          navigate('/historique');
        } finally {
          setLoadingReport(false);
        }
      };
      fetchReport();
    }
  }, [editMode, reportId, navigate]);

  useEffect(() => {
    if (editMode || !currentAgent?.id || draftDecisionMade) {
      return;
    }

    let mounted = true;
    const applyDraftData = (data) => {
      setFormData(prev => ({
        ...prev,
        ...data,
        ecoles_controle: data.ecoles_controle || {},
        ecole_porte_secours: data.ecole_porte_secours || '',
        etage2_controle: data.etage2_controle || {},
        sous_sols_controle: normalizeSousSolsControle(data.sous_sols_controle || prev.sous_sols_controle),
        reception_controle: data.reception_controle || prev.reception_controle,
        restaurant_controle: data.restaurant_controle || prev.restaurant_controle,
        cuisine_controle: data.cuisine_controle || prev.cuisine_controle,
        ecoles_ras: data.ecoles_ras || {},
        etage2_ras: data.etage2_ras || {},
        reception_ras: data.reception_ras || {},
        temperature_checks: data.temperature_checks || prev.temperature_checks,
        manoeuvres: data.manoeuvres || {},
        manoeuvre_times: data.manoeuvre_times || {},
        zone_times: data.zone_times || prev.zone_times,
        id: data.id || prev.id,
        started_by_agent_id: data.started_by_agent_id || '',
        started_by_agent_name: data.started_by_agent_name || '',
        transferred_to_agent_id: data.transferred_to_agent_id || '',
        transferred_to_agent_name: data.transferred_to_agent_name || '',
        controle_exterieurs: data.controle_exterieurs || prev.controle_exterieurs,
        controle_internes_hotel: data.controle_internes_hotel || prev.controle_internes_hotel,
        observations_etages: data.observations_etages || prev.observations_etages,
        incidents: data.incidents || [],
        material_checklist: data.material_checklist || prev.material_checklist,
      }));
    };
    const loadDraft = async () => {
      try {
        const decision = await loadBestDraft(currentAgent.id);
        if (!mounted || decision.action === 'none' || decision.action === 'submitted' || decision.action === 'transferred') {
          if (decision.action === 'submitted') setDraftStatus(decision.message || 'Rapport envoyé');
          if (decision.action === 'transferred') {
            setDraftStatus(decision.message);
            toast.info(decision.message);
          }
          if (decision.action === 'none') {
            const ui = readSessionDraftUi(currentAgent.id);
            if (ui) applyUi(ui);
          }
          return;
        }
        if (decision.action === 'restore' && decision.data) {
          applyDraftData(decision.data);
          applyUi(newerDraftUi(readSessionDraftUi(currentAgent.id), decision.ui));
          setDraftStatus(decision.message || 'Brouillon repris');
          setConflictKeys(decision.conflictKeys || []);
          return;
        }
        if (decision.action !== 'confirm-server' || !decision.data) return;

        const updatedAt = decision.updatedAt
          ? formatDateTime24(decision.updatedAt, { dateStyle: 'short', timeStyle: 'short' })
          : 'date inconnue';
        const autoResumeDraft = new URLSearchParams(location.search).get('resume') === '1';
        const shouldRestore = autoResumeDraft || window.confirm(
          `Un brouillon non terminé existe pour ce rapport (dernière sauvegarde: ${updatedAt}). Voulez-vous le reprendre ?`
        );

        if (shouldRestore) {
          applyDraftData(decision.data);
          applyUi(newerDraftUi(readSessionDraftUi(currentAgent.id), decision.ui));
          await adoptServerDraft(currentAgent.id, decision.data, decision.updatedAt, captureUi());
          setDraftStatus(`Brouillon repris (${updatedAt})`);
        } else {
          clearSessionDraftUi(currentAgent.id);
          await discardDraft(currentAgent.id);
          setDraftStatus('Ancien brouillon supprimé');
        }
      } catch (error) {
        console.error('Erreur chargement brouillon:', error);
      } finally {
        if (mounted) setDraftDecisionMade(true);
      }
    };

    loadDraft();
    return () => { mounted = false; };
  }, [applyUi, captureUi, currentAgent?.id, draftDecisionMade, editMode, location.search]);

  // Options prédéfinies
  const controleOptions = ['RAS', 'Portes non verrouillées', 'Lumières allumées', 'Projecteurs allumés', 'PCs allumés', 'Poubelles non vidées', 'Fenêtres ouvertes', 'Stores ouverts'];
  const controleOptionsRezEtPorte = ['RAS', 'Portes non verrouillées', 'Lumières allumées', 'Projecteurs allumés', 'PCs allumés', 'Poubelles non vidées', 'Fenêtres ouvertes', 'Stores ouverts', 'Porte de secours côté parking'];
  const controleOptionsSousSols = ['RAS', 'Porte ouverte', 'Porte non-verrouillée', 'Lumière allumée'];
  const frigosPredefined = ['Vitrine Pat. réception', 'Timbre office Inox', 'Congélateur glaces cuisine (laser)', 'Congélateur glaces Café vatel (laser)', 'Congélateur pizza Café vatel (laser)'];
  const sallesEcoles = ['001', '003', '004', '005', '006', '007', '008', '009', '010', '011', '013'];
  const chambresEtage2 = ['202a', '202b', '203', '204'];
  const sallesReception = ['001', '005', '016'];
  const controleOptionsReception = ['RAS', 'Portes non verrouillées', 'Lumières allumées'];
  // Le bureau de la direction est un local unique: on relève son état, pas des salles.
  const controleOptionsBureau = ['RAS', 'Porte ouverte', 'Lumière allumée'];
  // Une baie coulissante n'a ni porte ni éclairage.
  const controleOptionsFenetres = ['RAS', 'Fenêtres ouvertes', 'Fenêtres non verrouillées'];
  const controleOptionsReceptionFenetres = controleOptionsFenetres;
  const controleOptionsRestaurant = controleOptionsFenetres;
  const controleOptionsCuisine = ['RAS', 'Porte ouverte', 'Porte non-verrouillée', 'Lumière allumée'];

  // Configuration personnalisable des zones (panneau admin), avec valeurs par défaut.
  const cfg = reportConfig || {};
  const ecoleRooms = zoneList(cfg.ecoles?.rooms, sallesEcoles);
  const ecoleOptions = zoneList(cfg.ecoles?.options, controleOptions);
  const etage2Rooms = zoneList(cfg.etage2?.rooms, chambresEtage2);
  const etage2Options = zoneList(cfg.etage2?.options, controleOptions);
  const receptionRooms = zoneList(cfg.reception?.rooms, sallesReception);
  const receptionOptions = zoneList(cfg.reception?.options, controleOptionsReception);
  const receptionBureauOptions = zoneList(cfg.reception?.bureau_options, controleOptionsBureau);
  const receptionFenetresOptions = zoneList(cfg.reception?.fenetres_options, controleOptionsReceptionFenetres);
  const restaurantOptions = zoneList(cfg.restaurant?.options, controleOptionsRestaurant);
  const cuisineOptions = zoneList(cfg.cuisine?.options, controleOptionsCuisine);
  const sousSolsOptions = zoneList(cfg.sous_sols?.options, controleOptionsSousSols);
  const defaultRestaurantItems = ['Fenêtres coulissantes restaurant'];
  const defaultCuisineItems = ['Porte de secours et livraison côté parking'];
  const defaultSousSolsItems = [
    'Porte couloir accès SAS >> WC',
    'Cave',
    'Chambres froides',
    'Local Economat',
    'Local Buanderie',
    'Local Pâtisserie',
    'Garage porte vitrée',
    'Garage porte automatiques',
    'Salle Carnotzet',
    'Salle S03',
    'Dépôt sous-sol',
    'Contrôle du café Vatel (fermeture 23:00h)',
    'Fermeture salle Étudiants (student life)',
  ];
  const restaurantItems = zoneList(cfg.restaurant?.items, defaultRestaurantItems);
  const cuisineItems = zoneList(cfg.cuisine?.items, defaultCuisineItems);
  const sousSolsItems = zoneList(cfg.sous_sols?.items, defaultSousSolsItems)
    .map(item => (item === 'Dépôt boisson A2' ? 'Cave' : item));

  const normalizeSousSolsControle = (ctrl) => {
    const next = { ...(ctrl || {}) };
    if (Object.prototype.hasOwnProperty.call(next, 'Dépôt boisson A2')) {
      if (!String(next.Cave || '').trim()) next.Cave = next['Dépôt boisson A2'];
      delete next['Dépôt boisson A2'];
    }
    return next;
  };
  const ensureItemKeys = (ctrl, items) => {
    const next = { ...(ctrl || {}) };
    (items || []).forEach(item => {
      if (!(item in next)) next[item] = '';
    });
    return next;
  };

  // Aligner les clés des zones items avec la config admin (Cave, Chambres froides, etc.).
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      restaurant_controle: ensureItemKeys(prev.restaurant_controle, restaurantItems),
      cuisine_controle: ensureItemKeys(prev.cuisine_controle, cuisineItems),
      sous_sols_controle: ensureItemKeys(normalizeSousSolsControle(prev.sous_sols_controle), sousSolsItems),
      ecoles_ras: prev.ecoles_ras || {},
      etage2_ras: prev.etage2_ras || {},
      reception_ras: prev.reception_ras || {},
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig]);
  
  const observationsEtagesOptions = FLOOR_OBSERVATION_OPTIONS;

  const [formData, setFormData] = useState({
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : '',
    date_start: new Date().toLocaleDateString('fr-CH'),
    date_end: new Date().toLocaleDateString('fr-CH'),
    shift_start: '',
    shift_end: '',
    reception_name: '',
    reception_contact: '',
    reception_departure: '',
    ecoles_controle: {},
    ecoles_remarks: {},
    ecoles_notes: {},
    ecole_porte_secours: '',
    etage2_controle: {},
    etage2_remarks: {},
    etage2_notes: {},
    reception_controle: { 'Bureau direction': '', 'Fenêtres coulissantes': '' },
    reception_remarks: {},
    reception_notes: {},
    restaurant_remarks: {},
    cuisine_remarks: {},
    sous_sols_remarks: {},
    observations_remarks: {},
    restaurant_controle: { 'Fenêtres coulissantes restaurant': '' },
    cuisine_controle: { 'Porte de secours et livraison côté parking': '' },
    sous_sols_controle: {
      'Porte couloir accès SAS >> WC': '',
      'Cave': '',
      'Chambres froides': '',
      'Local Economat': '',
      'Local Buanderie': '',
      'Local Pâtisserie': '',
      'Garage porte vitrée': '',
      'Garage porte automatiques': '',
      'Salle Carnotzet': '',
      'Salle S03': '',
      'Dépôt sous-sol': '',
      'Contrôle du café Vatel (fermeture 23:00h)': '',
      'Fermeture salle Étudiants (student life)': ''
    },
    ecoles_ras: {},
    etage2_ras: {},
    reception_ras: {},
    temperature_checks: frigosPredefined.map(location => ({ location, temperature: '', time: '' })),
    manoeuvres: {},
    manoeuvre_times: {},
    zone_times: { ecoles: '', etage2: '', reception: '', restaurant: '', cuisine: '', sous_sols: '' },
    controle_exterieurs: Array(6).fill(''),
    controle_internes_hotel: Array(6).fill(''),
    sous_sols_passages: {},
    observations_etages: { 
      'Sous-sol': '', 
      'Rez-de-chaussée': '', 
      '1er': '', 
      'Buanderie étudiants': '',
      'Administration 120': '',
      '2ème': '', 
      'Vestiaires f/h': '',
      '3ème': '', 
      '4ème': '', 
      '5ème': '' 
    },
    remise_materiel_matin: '',
    remise_materiel_personnel: '',
    remise_materiel_heure: '',
    remise_materiel_heure_agent: '',
    started_by_agent_id: '',
    started_by_agent_name: '',
    transferred_to_agent_id: '',
    transferred_to_agent_name: '',
    transfer_time: '',
    transfer_notes: '',
    incidents: [],
    material_checklist: { 'iPhone en charge': false, 'Pass général': false, 'Lampe de poche': false, 'Tiroir fermé à clé et clé retirée': false, 'Autre': false }
  });

  useEffect(() => {
    const reportKey = editMode ? reportId : '';
    if (!reportKey || !currentAgent?.id) {
      setServerPhotos([]);
      return undefined;
    }
    let cancelled = false;
    desktopApi.listReportPhotos(reportKey)
      .then(async (rows) => {
        const shown = [];
        for (const row of rows || []) {
          const blob = await desktopApi.getReportPhotoFile(reportKey, row.id);
          shown.push({ id: row.id, url: URL.createObjectURL(blob) });
        }
        if (!cancelled) setServerPhotos(shown);
      })
      .catch(() => {
        if (!cancelled) setServerPhotos([]);
      });
    return () => { cancelled = true; };
  }, [editMode, reportId, currentAgent?.id]);

  const normalizeTime = (value) => {
    if (!value) return '';
    const v = String(value).trim();
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    if (/^\d{4}$/.test(v)) return `${v.slice(0, 2)}:${v.slice(2)}`;
    return v;
  };

  const normalizeReportTimes = useCallback((data) => ({
    ...data,
    shift_start: normalizeTime(data.shift_start),
    shift_end: normalizeTime(data.shift_end),
    transfer_time: normalizeTime(data.transfer_time),
    reception_contact: normalizeTime(data.reception_contact),
    reception_departure: normalizeTime(data.reception_departure),
    remise_materiel_heure: normalizeTime(data.remise_materiel_heure),
    remise_materiel_heure_agent: normalizeTime(data.remise_materiel_heure_agent),
    zone_times: Object.fromEntries(
      Object.entries(data.zone_times || {}).map(([k, v]) => [k, normalizeTime(v)])
    ),
    temperature_checks: (data.temperature_checks || []).map(t => ({
      ...t,
      time: normalizeTime(t?.time),
    })),
    manoeuvre_times: Object.fromEntries(
      Object.entries(data.manoeuvre_times || {}).map(([k, v]) => [k, normalizeTime(v)])
    ),
    controle_exterieurs: (data.controle_exterieurs || []).map(normalizeTime),
    controle_internes_hotel: (data.controle_internes_hotel || []).map(normalizeTime),
    sous_sols_passages: Object.fromEntries(
      Object.entries(data.sous_sols_passages || {}).map(([k, arr]) => [
        k,
        (Array.isArray(arr) ? arr : []).map(normalizeTime),
      ])
    ),
    incidents: (data.incidents || []).map(inc => ({
      ...inc,
      time: normalizeTime(inc?.time),
    })),
  }), []);

  const cleanTransferMetadata = useCallback((data) => {
    if (
      !data.transfer_time &&
      data.started_by_agent_name &&
      data.started_by_agent_name === data.agent_name
    ) {
      return {
        ...data,
        started_by_agent_id: '',
        started_by_agent_name: '',
        transferred_to_agent_id: '',
        transferred_to_agent_name: '',
        transfer_notes: '',
      };
    }
    return data;
  }, []);

  const buildDraftPayload = useCallback(() => cleanTransferMetadata(normalizeReportTimes({
    ...formData,
    agent_id: currentAgent?.id,
    agent_name: currentAgent?.name,
  })), [cleanTransferMetadata, currentAgent?.id, currentAgent?.name, formData, normalizeReportTimes]);

  const formDataIdRef = useRef('');
  useEffect(() => { formDataIdRef.current = formData.id || ''; }, [formData.id]);
  // Rapport passé à un collègue depuis un autre appareil : on cesse d'enregistrer
  // cette copie et on revient à l'accueil.
  const leaveTransferredReport = useCallback((message) => {
    transferLockedRef.current = true;
    setFormTouched(false);
    toast.info(message || 'Ce rapport a été transféré à un collègue.');
    navigate('/dashboard');
  }, [navigate]);
  useEffect(() => {
    if (editMode || !currentAgent?.id) return undefined;
    return subscribeSync((agentId, snapshot) => {
      if (agentId !== currentAgent.id || !snapshot?.transferredReportId) return;
      if (transferLockedRef.current) return;
      if (String(snapshot.transferredReportId) !== String(formDataIdRef.current || '')) return;
      leaveTransferredReport(snapshot.transferredMessage);
    });
  }, [currentAgent?.id, editMode, leaveTransferredReport]);

  const saveDraft = useCallback(async ({ silent = false } = {}) => {
    if (editMode || !currentAgent?.id || transferLockedRef.current || abandonLockedRef.current) return;
    setDraftSaving(true);
    try {
      const result = await rememberDraft(currentAgent.id, buildDraftPayload(), { ui: captureUi() });
      if (result.transferred) {
        leaveTransferredReport(result.message);
        return;
      }
      setDraftStatus(result.message || 'Brouillon enregistré');
      setConflictKeys(result.conflictKeys || []);
      if (!result.ok) {
        if (!silent) toast.error(result.message || 'Erreur lors de la sauvegarde du brouillon');
        return;
      }
      setFormTouched(false);
      if (!silent) {
        if (result.conflictKeys?.length) toast.warning(result.toast || result.message);
        else toast.success(result.toast || 'Brouillon sauvegardé');
      }
    } catch (error) {
      setDraftStatus('Erreur de sauvegarde du brouillon');
      if (!silent) toast.error('Erreur lors de la sauvegarde du brouillon');
    } finally {
      setDraftSaving(false);
    }
  }, [buildDraftPayload, captureUi, currentAgent?.id, editMode, leaveTransferredReport]);

  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  useEffect(() => {
    if (editMode || !draftDecisionMade || !formTouched || !currentAgent?.id || transferLockedRef.current || abandonLockedRef.current) return undefined;
    const timer = setTimeout(() => {
      saveDraft({ silent: true });
    }, 800);
    return () => clearTimeout(timer);
  }, [formData, formTouched, draftDecisionMade, editMode, currentAgent?.id, saveDraft]);

  useEffect(() => {
    if (editMode || !draftDecisionMade || !currentAgent?.id || transferLockedRef.current || abandonLockedRef.current) return undefined;
    const ui = captureUi();
    writeSessionDraftUi(currentAgent.id, ui);
    const timer = setTimeout(() => {
      patchDraftUi(currentAgent.id, ui).catch(() => null);
    }, 200);
    return () => clearTimeout(timer);
  }, [captureUi, currentAgent?.id, draftDecisionMade, editMode, mobileStep, openSections]);

  useEffect(() => {
    if (editMode || !currentAgent?.id) return undefined;
    let timer = 0;
    const persistUi = () => {
      if (transferLockedRef.current || abandonLockedRef.current || !draftDecisionMadeRef.current) return null;
      const ui = captureUi();
      writeSessionDraftUi(currentAgent.id, ui);
      patchDraftUi(currentAgent.id, ui).catch(() => null);
      return ui;
    };
    const onScroll = () => {
      if (Date.now() < ignoreScrollUntilRef.current) {
        const target = scrollYRef.current || 0;
        if (Math.abs((window.scrollY || 0) - target) > 2) {
          window.scrollTo({ top: target, left: 0, behavior: 'auto' });
        }
        return;
      }
      scrollYRef.current = window.scrollY || 0;
      if (!draftDecisionMadeRef.current) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(persistUi, 250);
    };
    scrollYRef.current = window.scrollY || scrollYRef.current || 0;
    const flushHidden = () => {
      persistUi();
      if (formTouchedRef.current) saveDraftRef.current?.({ silent: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushHidden();
    };
    const onPageShow = (event) => {
      if (!event.persisted || !draftDecisionMadeRef.current) return;
      const ui = readSessionDraftUi(currentAgent.id);
      if (ui) applyUi(ui);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushHidden);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushHidden);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('scroll', onScroll);
    };
  }, [applyUi, captureUi, currentAgent?.id, editMode]);

  const markFormTouched = () => {
    if (!editMode) setFormTouched(true);
  };

  const handleInputChange = (field, value) => {
    markFormTouched();
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'date_start' || field === 'date_end' || field === 'shift_start' || field === 'shift_end') {
        next.date_end = endDateForNight(next.date_start, next.date_end, next.shift_start, next.shift_end);
      }
      return next;
    });
  };
  const handleNestedChange = (section, key, value) => {
    markFormTouched();
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };
  
  // Chaque entrée est soit "Catégorie", soit "Catégorie (précision)".
  const splitEntries = (val) => (val || '').split(', ').map(s => s.trim()).filter(Boolean);
  const entryCategory = (entry) => {
    const m = entry.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    return m ? m[1].trim() : entry.trim();
  };
  const entryAnnotation = (entry) => {
    const m = entry.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    return m ? m[2].trim() : '';
  };

  const isOptionSelected = (section, key, option) => {
    return splitEntries(formData[section]?.[key]).some(p => entryCategory(p) === option);
  };
  const getOptionAnnotation = (section, key, option) => {
    const entry = splitEntries(formData[section]?.[key]).find(p => entryCategory(p) === option);
    return entry ? entryAnnotation(entry) : '';
  };
  const setOptionAnnotation = (section, key, option, annotation) => {
    markFormTouched();
    setFormData(prev => {
      const parts = splitEntries(prev[section]?.[key]);
      const ann = (annotation || '').trim();
      const newEntry = ann ? `${option} (${ann})` : option;
      const idx = parts.findIndex(p => entryCategory(p) === option);
      if (idx >= 0) parts[idx] = newEntry;
      else parts.push(newEntry);
      return { ...prev, [section]: { ...prev[section], [key]: parts.join(', ') } };
    });
  };
  const selectedCategories = (section, key) =>
    splitEntries(formData[section]?.[key]).map(entryCategory).filter(c => c && c !== 'RAS');

  // Vue "papier": une ligne par anomalie, puis sélection des salles concernées.
  const categoryOptions = (options) => (options || []).filter(opt => opt && opt !== 'RAS');
  const markAllCategoriesRas = (rasKey, section, rooms, options) => {
    markFormTouched();
    const categories = categoryOptions(options);
    setFormData(prev => {
      const nextCtrl = { ...(prev[section] || {}) };
      const nextRas = { ...(prev[rasKey] || {}) };
      categories.forEach((category) => {
        nextRas[category] = true;
        (rooms || []).forEach((room) => {
          const parts = splitEntries(nextCtrl[room]).filter((part) => entryCategory(part) !== category);
          nextCtrl[room] = parts.join(', ');
        });
      });
      return { ...prev, [section]: nextCtrl, [rasKey]: nextRas };
    });
  };
  const roomsSelectedForCategory = (section, rooms, category) =>
    (rooms || []).filter(room => isOptionSelected(section, room, category));
  const isCategoryRas = (rasKey, category) => Boolean(formData[rasKey]?.[category]);
  const toggleCategoryRas = (rasKey, section, rooms, category) => {
    markFormTouched();
    setFormData(prev => {
      const currentlyRas = Boolean(prev[rasKey]?.[category]);
      const nextCtrl = { ...(prev[section] || {}) };
      // RAS explicite: on retire les salles de cette catégorie.
      if (!currentlyRas) {
        rooms.forEach(room => {
          const parts = splitEntries(nextCtrl[room]).filter(p => entryCategory(p) !== category);
          nextCtrl[room] = parts.join(', ');
        });
      }
      return {
        ...prev,
        [section]: nextCtrl,
        [rasKey]: { ...(prev[rasKey] || {}), [category]: !currentlyRas },
      };
    });
  };
  const categorySummary = (section, rooms, category, rasKey) => {
    if (isCategoryRas(rasKey, category)) return 'RAS';
    const selected = roomsSelectedForCategory(section, rooms, category);
    if (!selected.length) return '—';
    return selected.map(room => {
      const ann = getOptionAnnotation(section, room, category);
      return ann ? `${room} (${ann})` : room;
    }).join(', ');
  };
  // Bouton RAS identique partout : vert quand il est choisi, gris sinon.
  // Il ne fixe que la réponse de l'élément et n'écrit jamais dans la remarque.
  const renderRasButton = ({ selected, onClick, testId, label = 'RAS', wide = false }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected ? 'true' : 'false'}
      className={`touch-choice inline-flex min-h-11 min-w-20 items-center justify-center rounded-md px-4 text-sm font-bold transition-colors ${wide ? 'w-full' : ''} ${
        selected
          ? 'bg-emerald-700 text-white'
          : 'bg-zinc-800 text-emerald-400 hover:bg-zinc-700'
      }`}
    >
      {label}
    </button>
  );
  const renderCategoryFirstZone = ({ section, rooms, options, remarksKey, rasKey, testPrefix }) => (
    <>
      <div className="mb-2">
        {renderRasButton({
          selected: categoryOptions(options).length > 0
            && categoryOptions(options).every((category) => isCategoryRas(rasKey, category)),
          onClick: () => markAllCategoriesRas(rasKey, section, rooms, options),
          testId: `${testPrefix}-all-ras`,
          label: 'Tout RAS',
          wide: true,
        })}
      </div>
      {categoryOptions(options).map(category => {
        const selectedRooms = roomsSelectedForCategory(section, rooms, category);
        const rasSelected = isCategoryRas(rasKey, category);
        return (
          <div key={category} className="p-4 bg-zinc-900/50 rounded-lg border border-border">
            <Label className="text-base font-semibold mb-2 block">{category}</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {renderRasButton({
                selected: rasSelected,
                onClick: () => toggleCategoryRas(rasKey, section, rooms, category),
                testId: `${testPrefix}-ras-${category}`,
              })}
              {rooms.map(room => (
                <button
                  key={room}
                  type="button"
                  onClick={() => {
                    markFormTouched();
                    setFormData(prev => {
                      const parts = splitEntries(prev[section]?.[room]);
                      const idx = parts.findIndex(p => entryCategory(p) === category);
                      if (idx >= 0) parts.splice(idx, 1);
                      else parts.push(category);
                      return {
                        ...prev,
                        [section]: { ...prev[section], [room]: parts.join(', ') },
                        [rasKey]: { ...(prev[rasKey] || {}), [category]: false },
                      };
                    });
                  }}
                  data-testid={`${testPrefix}-room-${category}-${room}`}
                  className={`touch-choice inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${
                    isOptionSelected(section, room, category)
                      ? 'bg-amber-700 text-white'
                      : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'
                  }`}
                >
                  {room}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-2" data-testid={`${testPrefix}-summary-${category}`}>
              {categorySummary(section, rooms, category, rasKey)}
            </p>
            {selectedRooms.map(room => (
              <div key={`${category}-${room}`} className="flex items-center gap-2 mb-1">
                <span className="text-xs text-muted-foreground min-w-[60px]">{room} :</span>
                <Input
                  placeholder="Précision (ex: x2)"
                  value={getOptionAnnotation(section, room, category)}
                  onChange={(e) => setOptionAnnotation(section, room, category, e.target.value)}
                  data-testid={`${testPrefix}-note-${room}-${category}`}
                  className="text-sm h-8 max-w-[180px]"
                />
              </div>
            ))}
          </div>
        );
      })}
      <div className="p-4 bg-zinc-900/50 rounded-lg border border-border">
        <Label className="text-sm font-semibold mb-2 block">Remarques par salle (optionnel)</Label>
        <div className="space-y-2">
          {rooms.map(room => (
            <div key={room} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground min-w-[60px]">{room}</span>
              <Input
                placeholder="Remarque (texte libre)..."
                value={formData[remarksKey]?.[room] || ''}
                onChange={(e) => handleNestedChange(remarksKey, room, e.target.value)}
                data-testid={`${testPrefix}-remark-${room}`}
                aria-label={`Remarque ${room}`}
                className="text-sm"
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const chooseControlOption = (section, item, option) => {
    markFormTouched();
    setFormData(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [item]: toggleControlOption(prev[section]?.[item], option) },
    }));
  };
  const legacyControlNote = (section, item, options) => (
    unmatchedControlParts(formData[section]?.[item], options)
  );
  // Contrôle portant sur un local unique: on relève son état, sans liste de salles.
  const renderDedicatedItem = ({ section, item, options, remarksKey, testPrefix }) => (
    <div className="p-4 bg-zinc-900/50 rounded-lg border border-border" data-testid={`${testPrefix}-row`}>
      <Label className="text-sm font-semibold mb-2 block">{item}</Label>
      {legacyControlNote(section, item, options).length > 0 && (
        <p className="mb-2 text-sm text-foreground" data-testid={`${testPrefix}-legacy`}>
          Déjà noté : {legacyControlNote(section, item, options).join(', ')}
        </p>
      )}
      <div className="flex flex-wrap gap-2 mb-2">
        {renderRasButton({
          selected: isOptionSelected(section, item, 'RAS'),
          onClick: () => chooseControlOption(section, item, 'RAS'),
          testId: `${testPrefix}-ras`,
        })}
        {options.filter(opt => opt !== 'RAS').map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => chooseControlOption(section, item, opt)}
            data-testid={`${testPrefix}-option-${opt}`}
            className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${isOptionSelected(section, item, opt) ? 'bg-primary text-primary-foreground' : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'}`}
          >
            {opt}
          </button>
        ))}
      </div>
      {selectedCategories(section, item).filter(cat => options.includes(cat)).map(cat => (
        <div key={cat} className="flex items-center gap-2 mb-1">
          <span className="text-xs text-muted-foreground min-w-[140px]">{cat} :</span>
          <Input
            placeholder="Précision (ex: x2)"
            value={getOptionAnnotation(section, item, cat)}
            onChange={(e) => setOptionAnnotation(section, item, cat, e.target.value)}
            data-testid={`input-${testPrefix}-note`}
            className="text-sm h-8 max-w-[180px]"
          />
        </div>
      ))}
      <Input
        placeholder="Remarque (texte libre)..."
        value={formData[remarksKey]?.[item] || ''}
        onChange={(e) => handleNestedChange(remarksKey, item, e.target.value)}
        data-testid={`input-${testPrefix}-remark`}
        aria-label={`Remarque ${item}`}
        className="text-sm mt-1"
      />
    </div>
  );

  const handleTemperatureChange = (index, field, value) => {
    markFormTouched();
    setFormData(prev => {
      const newTemps = [...prev.temperature_checks];
      newTemps[index] = { ...newTemps[index], [field]: value };
      return { ...prev, temperature_checks: newTemps };
    });
  };
  
  // Contrôles heures
  const handleControleHeureChange = (field, index, value) => {
    markFormTouched();
    setFormData(prev => {
      const newHeures = [...prev[field]];
      newHeures[index] = value;
      return { ...prev, [field]: newHeures };
    });
  };
  const handleAddControleHeure = (field, value = '') => {
    markFormTouched();
    setFormData(prev => {
      const list = [...(prev[field] || [])];
      const emptyIndex = list.findIndex((item) => !item);
      if (value && emptyIndex >= 0) list[emptyIndex] = value;
      else list.push(value);
      return { ...prev, [field]: list };
    });
  };
  const handleRemoveControleHeure = (field, index) => {
    markFormTouched();
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  // Horaires de passage multiples par élément de contrôle (ex: sous-sols)
  const handleAddPassage = (zoneField, itemKey, value = '') => {
    markFormTouched();
    setFormData(prev => {
      const zone = { ...(prev[zoneField] || {}) };
      const list = Array.isArray(zone[itemKey]) ? [...zone[itemKey]] : [];
      const emptyIndex = list.findIndex((item) => !item);
      if (value && emptyIndex >= 0) list[emptyIndex] = value;
      else list.push(value);
      zone[itemKey] = list;
      return { ...prev, [zoneField]: zone };
    });
  };
  const handlePassageChange = (zoneField, itemKey, index, value) => {
    markFormTouched();
    setFormData(prev => {
      const zone = { ...(prev[zoneField] || {}) };
      const list = Array.isArray(zone[itemKey]) ? [...zone[itemKey]] : [];
      list[index] = value;
      zone[itemKey] = list;
      return { ...prev, [zoneField]: zone };
    });
  };
  const handleRemovePassage = (zoneField, itemKey, index) => {
    markFormTouched();
    setFormData(prev => {
      const zone = { ...(prev[zoneField] || {}) };
      const list = Array.isArray(zone[itemKey]) ? [...zone[itemKey]] : [];
      zone[itemKey] = list.filter((_, i) => i !== index);
      return { ...prev, [zoneField]: zone };
    });
  };
  
  // Incidents
  const handleAddIncident = () => {
    markFormTouched();
    const newIncident = {
      id: Date.now(),
      time: formatDateTime24(new Date(), { hour: '2-digit', minute: '2-digit' }),
      description: ''
    };
    setFormData(prev => ({ ...prev, incidents: [...prev.incidents, newIncident] }));
  };
  const remarkGroups = Object.entries(remarkTemplates || DEFAULT_REMARK_TEMPLATES)
    .filter(([, list]) => Array.isArray(list) && list.length);
  // {reception} reprend le nom déjà saisi en tête de rapport.
  const resolveTemplate = (text) => String(text).replace(
    /\{reception\}/g,
    (formData.reception_name || '').trim() || '…',
  );
  const handleAddIncidentFromTemplate = (text) => {
    const newIncident = {
      id: Date.now() + Math.random(),
      time: formatDateTime24(new Date(), { hour: '2-digit', minute: '2-digit' }),
      description: resolveTemplate(text),
    };
    setFormData(prev => ({ ...prev, incidents: [...prev.incidents, newIncident] }));
  };
  const handleIncidentChange = (id, field, value) => {
    markFormTouched();
    setFormData(prev => ({
      ...prev,
      incidents: prev.incidents.map(inc => inc.id === id ? { ...inc, [field]: value } : inc)
    }));
  };
  const handleRemoveIncident = (id) => {
    markFormTouched();
    setFormData(prev => ({ ...prev, incidents: prev.incidents.filter(inc => inc.id !== id) }));
  };
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    markFormTouched();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
    if (dragIndex === dropIndex) return;
    
    setFormData(prev => {
      const newIncidents = [...prev.incidents];
      const [removed] = newIncidents.splice(dragIndex, 1);
      newIncidents.splice(dropIndex, 0, removed);
      return { ...prev, incidents: newIncidents };
    });
  };

  const blockIfServiceHoursMissing = () => {
    const issue = serviceHoursIssue(formData);
    if (!issue) {
      setHoursError('');
      return false;
    }
    setHoursError(issue.message);
    setMobileStep(0);
    toast.error(issue.message);
    window.setTimeout(() => {
      const field = document.getElementById(issue.fieldId);
      if (!field) return;
      field.focus();
      field.scrollIntoView({ block: 'center' });
    }, 50);
    return true;
  };

  const handleSubmit = async () => {
    if (blockIfServiceHoursMissing()) return;
    setLoading(true);
    let payload = null;
    let createdId = '';
    let mailState = null;
    try {
      payload = cleanTransferMetadata(normalizeReportTimes({
        ...formData,
        agent_id: currentAgent.id,
        agent_name: currentAgent.name,
      }));
      if (editMode && reportId) {
        await desktopApi.updateReport(reportId, payload);
        createdId = reportId;
      } else {
        try {
          const created = await desktopApi.createReport(payload);
          createdId = created.id;
          mailState = created.mail || null;
        } catch (createError) {
          if (createError?.response?.status === 409 && payload.id
            && createError?.response?.data?.detail?.code !== 'report_transferred') {
            await desktopApi.updateReport(payload.id, payload);
            createdId = payload.id;
          } else {
            throw createError;
          }
        }
      }
      const keepLocal = submitKeepsLocalDraft(editMode);
      if (!keepLocal) {
        await writeLocalDraft({
          agentId: currentAgent.id,
          data: payload,
          baseData: payload,
          pending: false,
          pendingSubmit: true,
          submittedReportId: createdId,
          localUpdatedAt: new Date().toISOString(),
        });
      }
      await uploadQueuedPhotos(currentAgent.id, createdId);
      const note = mailSubmitMessage(mailState, editMode);
      if (!keepLocal) {
        await desktopApi.deleteReportDraft(currentAgent.id).catch(() => null);
        clearSessionDraftUi(currentAgent.id);
        await clearLocalAfterSubmit(currentAgent.id, note);
        await clearAgentPhotos(currentAgent.id).catch(() => null);
      }
      toast.success(note);
      setFormTouched(false);
      navigate('/historique');
    } catch (error) {
      if (createdId) {
        toast.success('Rapport enregistré. Les photos restantes partiront à la prochaine ouverture.');
        setFormTouched(false);
        navigate('/historique');
        return;
      }
      const transferredAway = ownershipFromConflict(error?.response?.data?.detail);
      if (transferredAway && !editMode) {
        const moved = await reconcileTransferredDraft(currentAgent.id, transferredAway);
        leaveTransferredReport(moved?.message || error.response.data.detail.message);
        return;
      }
      if (!error?.response && !editMode && payload) {
        await rememberDraft(currentAgent.id, payload, { submit: true });
        toast.success('Hors réseau. Le rapport sera envoyé dès le retour du signal.');
        setFormTouched(false);
        navigate('/dashboard');
        return;
      }
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim()
        ? detail
        : (editMode ? 'Erreur lors de la modification du rapport' : 'Erreur lors de la création du rapport'));
    } finally {
      setLoading(false);
    }
  };

  const handleTransferReport = async () => {
    const targetAgent = agentsList.find(agent => agent.id === transferAgentId);
    if (!targetAgent) {
      toast.error('Veuillez choisir l\'agent qui reprend le rapport');
      return;
    }

    const reportKey = (editMode && reportId) ? reportId : formData.id;
    transferLockedRef.current = true;
    setTransferSaving(true);
    try {
      const queued = await listReportPhotos(currentAgent.id).catch(() => []);
      for (const photo of queued) {
        if (!photo.reportId && reportKey) await tagReportPhoto(photo.id, reportKey);
      }
      const pending = photosForReport(await listReportPhotos(currentAgent.id).catch(() => []), reportKey);
      const decision = transferPhotoDecision({
        online: typeof navigator === 'undefined' ? true : navigator.onLine,
        pendingCount: pending.length,
      });
      if (!decision.ok) {
        transferLockedRef.current = false;
        toast.error(decision.message);
        return;
      }

      const transferTime = formatDateTime24(new Date(), { hour: '2-digit', minute: '2-digit' });
      const transferPayload = normalizeReportTimes({
        ...formData,
        id: reportKey,
        started_by_agent_id: formData.started_by_agent_id || currentAgent.id,
        started_by_agent_name: formData.started_by_agent_name || currentAgent.name,
        agent_id: targetAgent.id,
        agent_name: targetAgent.name,
        transferred_to_agent_id: targetAgent.id,
        transferred_to_agent_name: targetAgent.name,
        transfer_time: formData.transfer_time || transferTime,
        transfer_notes: formData.transfer_notes || '',
      });

      if (pending.length) {
        try {
          await desktopApi.createReport({
            ...transferPayload,
            agent_id: currentAgent.id,
            agent_name: currentAgent.name,
          });
        } catch (createError) {
          if (createError?.response?.status !== 409) throw createError;
        }
        await uploadQueuedPhotos(currentAgent.id, reportKey);
      }

      await desktopApi.transferReport(currentAgent.id, {
        target_agent_id: targetAgent.id,
        report_id: reportKey || '',
        data: transferPayload,
      });
      const local = await readLocalDraft(currentAgent.id).catch(() => null);
      const localId = String(local?.data?.id || '');
      const sameReport = !reportKey || !localId || localId === String(reportKey);
      if (!editMode || sameReport) {
        await deleteLocalDraft(currentAgent.id).catch(() => null);
        clearSessionDraftUi(currentAgent.id);
      }
      const leftover = photosForReport(
        await listReportPhotos(currentAgent.id).catch(() => []),
        reportKey,
      );
      for (const photo of leftover) {
        await deleteReportPhoto(photo.id).catch(() => null);
      }
      setTransferAgentId('');
      setFormTouched(false);
      toast.success(`Rapport transféré à ${targetAgent.name}. Vous restez connecté.`);
      navigate('/dashboard');
    } catch (error) {
      transferLockedRef.current = false;
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim()
        ? detail
        : 'Des photos n\'ont pas pu être envoyées. Le transfert est annulé.');
    } finally {
      setTransferSaving(false);
    }
  };

  const currentStepId = REPORT_STEPS[Math.min(mobileStep, REPORT_STEPS.length - 1)].id;
  const sectionRows = REPORT_STEPS.map((step) => ({
    ...step,
    status: sectionStatus(step.id, formData, {
      photoCount: localPhotos.length + serverPhotos.length,
      catalog: {
        ecoles: { rooms: ecoleRooms, options: ecoleOptions, door: true },
        etage2: { rooms: etage2Rooms, options: etage2Options },
        reception: {
          rooms: receptionRooms,
          options: receptionOptions,
          extraItems: ['Bureau direction', 'Fenêtres coulissantes'],
        },
        restaurant: { items: restaurantItems },
        cuisine: { items: cuisineItems },
        sousSols: { items: sousSolsItems },
        manoeuvres: manoeuvresList.map((manoeuvre) => manoeuvre.name),
        floors: Object.keys(observationsEtagesOptions),
        materiel: Object.keys(formData.material_checklist || {}),
      },
    }),
  }));
  const filledCount = sectionRows.filter((step) => step.status === 'filled').length;
  const completedCount = sectionRows.filter((step) => step.status === 'completed').length;
  const sectionSummary = [
    completedCount ? `${completedCount} complétée${completedCount > 1 ? 's' : ''}` : '',
    filledCount ? `${filledCount} renseignée${filledCount > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ') || '0 renseignée';
  const jumpToSection = (id) => {
    const index = REPORT_STEPS.findIndex((step) => step.id === id);
    if (index >= 0) setMobileStep(index);
    if (id !== 'general' && id !== 'finaliser') {
      setOpenSections((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
    setSectionMenuOpen(false);
    window.setTimeout(() => {
      document.getElementById(`section-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
  };
  const sectionMenu = (
    <div className={isMobile ? 'mt-2' : 'mb-4'}>
      <button
        type="button"
        data-testid="btn-section-menu"
        onClick={() => setSectionMenuOpen((open) => !open)}
        className="flex h-12 w-full items-center justify-between rounded-md border border-border bg-card px-3 text-left"
        aria-expanded={sectionMenuOpen}
      >
        <span className="font-semibold">Sections</span>
        <span className="text-sm text-muted-foreground">{sectionSummary}</span>
      </button>
      {sectionMenuOpen && (
        <ul data-testid="section-menu" className="mt-2 max-h-[50vh] overflow-y-auto rounded-md border border-border bg-card">
          {sectionRows.map((step) => (
            <li key={step.id}>
              <button
                type="button"
                data-testid={`section-jump-${step.id}`}
                onClick={() => jumpToSection(step.id)}
                className={`flex min-h-12 w-full items-center justify-between gap-3 px-3 py-2 text-left ${step.id === currentStepId && isMobile ? 'bg-primary/10' : ''}`}
              >
                <span className="font-semibold">{step.label}</span>
                {step.id !== 'finaliser' && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-semibold uppercase ${
                      step.status === 'completed'
                        ? 'rounded-full bg-primary/15 px-2 py-0.5 text-primary'
                        : step.status === 'filled'
                          ? 'text-primary'
                          : 'text-muted-foreground'
                    }`}
                    data-testid={`section-status-${step.id}`}
                    data-status={step.status}
                    aria-label={SECTION_STATUS_LABEL[step.status]}
                  >
                    {step.status === 'completed' && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                    {SECTION_STATUS_LABEL[step.status]}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  const stepClass = (id) => (isMobile && currentStepId !== id ? 'hidden' : '');
  const mobileAccordionValue = ['general', 'finaliser'].includes(currentStepId) ? [] : [currentStepId];
  useEffect(() => {
    if (isMobile || ['general', 'finaliser'].includes(currentStepId)) return;
    setOpenSections((prev) => (prev.includes(currentStepId) ? prev : [...prev, currentStepId]));
  }, [isMobile, currentStepId]);
  const legacyNoteEntries = (value) => {
    if (typeof value === 'string' && value.trim()) return [['', value.trim()]];
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return Object.entries(value)
      .map(([room, text]) => [String(room), String(text ?? '').trim()])
      .filter(([, text]) => text);
  };

  const renderLegacyNotes = () => {
    const groups = [
      ['ecoles_notes', 'Salles école', 'legacy-notes-ecoles'],
      ['etage2_notes', '2ème étage', 'legacy-notes-etage2'],
      ['reception_notes', 'Réception', 'legacy-notes-reception'],
    ].map(([key, label, testId]) => ({
      key,
      label,
      testId,
      entries: legacyNoteEntries(formData[key]),
    })).filter(group => group.entries.length > 0);
    if (!groups.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-6" data-testid="legacy-notes">
        <h2 className="text-lg font-semibold">Notes déjà enregistrées</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ces notes viennent d&apos;une version précédente du rapport. Elles sont conservées telles quelles.
        </p>
        <div className="mt-4 space-y-4">
          {groups.map(group => (
            <div key={group.key} data-testid={group.testId}>
              <p className="text-sm font-semibold text-foreground">{group.label}</p>
              <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                {group.entries.map(([room, text]) => (
                  <li key={`${group.key}-${room || text}`}>
                    {room ? <span className="font-medium text-foreground">{room} : </span> : null}
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderZoneTime = (zoneKey) => (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
      <Label className="text-sm font-semibold whitespace-nowrap">Heure du contrôle :</Label>
      <Input type="time" value={formData.zone_times?.[zoneKey] || ''} onChange={(e) => handleNestedChange('zone_times', zoneKey, e.target.value)} className="max-w-[150px] h-12" />
      <NowStamp onStamp={() => handleNestedChange('zone_times', zoneKey, nowHHMM())} />
    </div>
  );
  const keepPhoneVersion = async () => {
    const result = await resolveConflict(currentAgent.id, 'phone');
    if (result.data) {
      setConflictKeys([]);
      setDraftStatus(result.message);
    }
  };
  const takeServerVersion = async () => {
    const result = await resolveConflict(currentAgent.id, 'server');
    if (result.data) {
      setFormData(prev => ({ ...prev, ...result.data }));
      setConflictKeys([]);
      setDraftStatus(result.message);
      toast.success(result.message);
    }
  };

  // Affichage de chargement en mode édition
  if (loadingReport) {
    return (
      <div className="safe-screen min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Chargement du rapport...</p>
      </div>
    );
  }

  return (
    <div className="report-form safe-screen min-h-screen bg-background pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))] md:pb-16">
      <div className="max-w-5xl mx-auto p-4 md:p-10">
        {isMobile && (
          <div className="safe-sticky sticky z-30 -mx-4 mb-4 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
            <div className="flex items-center gap-2">
              <button type="button" disabled={mobileStep === 0} onClick={() => setMobileStep((step) => Math.max(0, step - 1))} className="h-12 min-w-16 rounded-md bg-secondary px-3 text-sm font-semibold disabled:opacity-40">
                Préc.
              </button>
              <div className="min-w-0 flex-1 text-center">
                <p className="text-xs text-muted-foreground">Étape {mobileStep + 1} / {REPORT_STEPS.length}</p>
                <p className="truncate text-lg font-bold uppercase" data-testid="mobile-step-label">{REPORT_STEPS[mobileStep].label}</p>
              </div>
              <button type="button" disabled={mobileStep >= REPORT_STEPS.length - 1} onClick={() => setMobileStep((step) => Math.min(REPORT_STEPS.length - 1, step + 1))} className="h-12 min-w-16 rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-40" data-testid="mobile-step-next">
                Suiv.
              </button>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
              <div className="h-full bg-primary" style={{ width: `${((mobileStep + 1) / REPORT_STEPS.length) * 100}%` }} />
            </div>
            {sectionMenu}
          </div>
        )}
        <div className="mb-6 md:mb-8">
          <button onClick={() => navigate(editMode ? '/historique' : '/dashboard')} data-testid="back-button" className="flex min-h-11 items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />{editMode ? 'Retour à l\'historique' : 'Retour à l\'accueil'}
          </button>
          <h1 className="text-3xl md:text-5xl tracking-tight uppercase font-bold mb-2">{editMode ? 'Modifier le Rapport' : 'Nouveau Rapport'}</h1>
          <p className="text-muted-foreground">Agent: {currentAgent?.name}</p>
          {!editMode && <div className="mt-3"><SyncStatus agentId={currentAgent?.id} /></div>}
          {conflictKeys.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-700 bg-amber-950/50 p-4" data-testid="draft-conflict">
              <p className="font-semibold text-amber-100">Deux versions du brouillon ont été fusionnées.</p>
              <p className="mt-1 text-sm text-amber-50/90">
                Votre saisie est conservée pour : {labelConflicts(conflictKeys).join(', ')}. Les champs vides ont repris l&apos;autre version.
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button type="button" onClick={keepPhoneVersion} className="h-12 rounded-md bg-primary px-4 font-semibold text-primary-foreground">Garder ma saisie</button>
                <button type="button" onClick={takeServerVersion} className="h-12 rounded-md bg-secondary px-4 font-semibold">Reprendre le serveur</button>
              </div>
            </div>
          )}
          {!editMode && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <button
                type="button"
                onClick={() => saveDraft({ silent: false })}
                disabled={draftSaving}
                data-testid="btn-save-draft"
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${draftSaving ? 'animate-spin' : ''}`} />
                {draftSaving ? 'Sauvegarde...' : 'Sauvegarder le brouillon'}
              </button>
              <DeleteDraftButton
                agentId={currentAgent?.id}
                reportId={formData.id || ''}
                onBeforeDelete={() => { abandonLockedRef.current = true; }}
                onFailed={() => { abandonLockedRef.current = false; }}
                onDeleted={() => {
                  setFormTouched(false);
                  navigate('/dashboard');
                }}
                className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 font-medium text-destructive-foreground hover:bg-destructive/90"
              />
              {draftStatus && <span className="text-muted-foreground" data-testid="draft-status">{draftStatus}</span>}
            </div>
          )}
        </div>

        {!isMobile && sectionMenu}
        <div className="space-y-6">
          <div id="section-general" className={`bg-card border border-border rounded-lg p-4 md:p-6 ${stepClass('general')}`}>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-wide">Informations Générales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="input-date-start" className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm text-left">
                      {formData.date_start || 'Sélectionner...'}
                      <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" locale={fr} selected={formData.date_start ? (() => { const p = formData.date_start.split('.'); return p.length === 3 ? new Date(p[2], p[1]-1, p[0]) : undefined; })() : undefined} onSelect={(date) => { if (date) handleInputChange('date_start', format(date, 'dd.MM.yyyy')); }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Date fin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="input-date-end" className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm text-left">
                      {formData.date_end || 'Sélectionner...'}
                      <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" locale={fr} selected={formData.date_end ? (() => { const p = formData.date_end.split('.'); return p.length === 3 ? new Date(p[2], p[1]-1, p[0]) : undefined; })() : undefined} onSelect={(date) => { if (date) handleInputChange('date_end', format(date, 'dd.MM.yyyy')); }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="shift-start">Prise de service</Label>
                <div className="flex gap-2">
                  <Input id="shift-start" type="time" value={formData.shift_start} onChange={(e) => handleInputChange('shift_start', e.target.value)} data-testid="input-shift-start" className="h-12" aria-invalid={Boolean(hoursError) && !String(formData.shift_start || '').trim()} aria-describedby={hoursError && serviceHoursIssue(formData) ? 'service-hours-error' : undefined} />
                  <NowStamp onStamp={() => handleInputChange('shift_start', nowHHMM())} />
                </div>
              </div>
              <div>
                <Label htmlFor="shift-end">Fin de service</Label>
                <div className="flex gap-2">
                  <Input id="shift-end" type="time" value={formData.shift_end} onChange={(e) => handleInputChange('shift_end', e.target.value)} data-testid="input-shift-end" className="h-12" aria-invalid={Boolean(hoursError) && !String(formData.shift_end || '').trim()} aria-describedby={hoursError && serviceHoursIssue(formData) ? 'service-hours-error' : undefined} />
                  <NowStamp onStamp={() => handleInputChange('shift_end', nowHHMM())} />
                </div>
              </div>
              {hoursError && serviceHoursIssue(formData) && (
                <p id="service-hours-error" role="alert" data-testid="service-hours-error" className="md:col-span-2 text-sm font-medium text-destructive">
                  {serviceHoursIssue(formData).message}
                </p>
              )}
              {formData.started_by_agent_name && formData.started_by_agent_name !== currentAgent?.name && (
                <div className="md:col-span-2 rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-muted-foreground">
                  Rapport commencé par <span className="font-semibold text-foreground">{formData.started_by_agent_name}</span>
                  {formData.transfer_time && `, transféré à ${formData.transfer_time}`}
                  {formData.transfer_notes && <div className="mt-1">Notes de transfert: {formData.transfer_notes}</div>}
                </div>
              )}
              <div>
                <Label>Réception (Nom)</Label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {receptionnistesList.map(name => (
                      <button key={name} type="button" onClick={() => handleInputChange('reception_name', name)}
                        className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${formData.reception_name === name ? 'bg-primary text-primary-foreground' : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'}`}>{name}</button>
                    ))}
                  </div>
                  <Input placeholder="Ou entrer un nom manuellement..." value={formData.reception_name} onChange={(e) => handleInputChange('reception_name', e.target.value)} data-testid="input-reception-name" aria-label="Nom de la réception" />
                </div>
              </div>
              <div><Label htmlFor="reception-contact">Contact réception</Label><Input id="reception-contact" type="time" value={formData.reception_contact} onChange={(e) => handleInputChange('reception_contact', e.target.value)} data-testid="input-reception-contact" /></div>
              <div><Label htmlFor="reception-departure">Départ réception</Label><Input id="reception-departure" type="time" value={formData.reception_departure} onChange={(e) => handleInputChange('reception_departure', e.target.value)} data-testid="input-reception-departure" /></div>
            </div>
          </div>

          {(!isMobile || currentStepId === 'general') && renderLegacyNotes()}

          <Accordion
            type="multiple"
            className="space-y-4"
            value={isMobile ? mobileAccordionValue : openSections}
            onValueChange={(next) => {
              if (!isMobile) setOpenSections(next);
            }}
          >
            <AccordionItem id="section-ecoles" value="ecoles" className={`bg-card border border-border rounded-lg ${stepClass('ecoles')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Salles École (IDIAP)</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('ecoles')}
                  <p className="text-xs text-muted-foreground">
                    Saisie comme sur le papier : choisissez l&apos;anomalie, puis cochez les salles concernées.
                  </p>
                  {renderCategoryFirstZone({
                    section: 'ecoles_controle',
                    rooms: ecoleRooms,
                    options: ecoleOptions,
                    remarksKey: 'ecoles_remarks',
                    rasKey: 'ecoles_ras',
                    testPrefix: 'ecole',
                  })}
                  <div className="p-4 bg-zinc-900/50 rounded-lg border border-primary/20">
                    <Label className="text-base font-semibold mb-2 block">Porte de secours côté parking</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {renderRasButton({
                        selected: formData.ecole_porte_secours === 'RAS',
                        onClick: () => handleInputChange('ecole_porte_secours', formData.ecole_porte_secours === 'RAS' ? '' : 'RAS'),
                        testId: 'ecole-porte-secours-ras',
                      })}
                      {PORTE_SECOURS_ANOMALIES.map(opt => (
                        <button key={opt} type="button" onClick={() => handleInputChange('ecole_porte_secours', formData.ecole_porte_secours === opt ? '' : opt)}
                          data-testid={`ecole-porte-secours-option-${opt}`}
                          className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${formData.ecole_porte_secours === opt ? 'bg-primary text-primary-foreground' : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'}`}>{opt}</button>
                      ))}
                    </div>
                    {/* Le texte libre ne reprend pas le bouton choisi : seul un texte saisi s'y affiche. */}
                    <Input placeholder="Ou observation personnalisée..." value={PORTE_SECOURS_CHOICES.includes(formData.ecole_porte_secours) ? '' : (formData.ecole_porte_secours || '')} onChange={(e) => handleInputChange('ecole_porte_secours', e.target.value)} className="text-sm" data-testid="input-ecole-porte-secours" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-etage2" value="etage2" className={`bg-card border border-border rounded-lg ${stepClass('etage2')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">2ème Étage</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('etage2')}
                  <p className="text-xs text-muted-foreground">
                    Saisie comme sur le papier : choisissez l&apos;anomalie, puis cochez les salles concernées.
                  </p>
                  {renderCategoryFirstZone({
                    section: 'etage2_controle',
                    rooms: etage2Rooms,
                    options: etage2Options,
                    remarksKey: 'etage2_remarks',
                    rasKey: 'etage2_ras',
                    testPrefix: 'etage2',
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-reception" value="reception" className={`bg-card border border-border rounded-lg ${stepClass('reception')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Zone Réception</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('reception')}
                  <p className="text-xs text-muted-foreground">
                    Saisie comme sur le papier : choisissez l&apos;anomalie, puis cochez les salles concernées.
                  </p>
                  {renderCategoryFirstZone({
                    section: 'reception_controle',
                    rooms: receptionRooms,
                    options: receptionOptions,
                    remarksKey: 'reception_remarks',
                    rasKey: 'reception_ras',
                    testPrefix: 'reception',
                  })}
                  {renderDedicatedItem({
                    section: 'reception_controle',
                    item: 'Bureau direction',
                    options: receptionBureauOptions,
                    remarksKey: 'reception_remarks',
                    testPrefix: 'reception-bureau',
                  })}
                  {renderDedicatedItem({
                    section: 'reception_controle',
                    item: 'Fenêtres coulissantes',
                    options: receptionFenetresOptions,
                    remarksKey: 'reception_remarks',
                    testPrefix: 'reception-fenetres',
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-restaurant" value="restaurant" className={`bg-card border border-border rounded-lg ${stepClass('restaurant')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Zone Restaurant</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('restaurant')}
                  {restaurantItems.map(key => (
                    <React.Fragment key={key}>
                      {renderDedicatedItem({
                        section: 'restaurant_controle',
                        item: key,
                        options: restaurantOptions,
                        remarksKey: 'restaurant_remarks',
                        testPrefix: `restaurant-${key}`,
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-cuisine" value="cuisine" className={`bg-card border border-border rounded-lg ${stepClass('cuisine')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Zone Cuisine</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('cuisine')}
                  {cuisineItems.map(key => (
                    <React.Fragment key={key}>
                      {renderDedicatedItem({
                        section: 'cuisine_controle',
                        item: key,
                        options: cuisineOptions,
                        remarksKey: 'cuisine_remarks',
                        testPrefix: `cuisine-${key}`,
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-sous-sols" value="sous-sols" className={`bg-card border border-border rounded-lg ${stepClass('sous-sols')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Sous-sols</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {renderZoneTime('sous_sols')}
                  {sousSolsItems.map(key => (
                    <div key={key} className="p-4 bg-zinc-900/50 rounded-lg border border-border" data-testid={`sous-sol-row-${key}`}>
                      <Label className="text-sm font-semibold mb-2 block">{key}</Label>
                      {legacyControlNote('sous_sols_controle', key, sousSolsOptions).length > 0 && (
                        <p className="mb-2 text-sm text-foreground" data-testid={`sous-sol-legacy-${key}`}>
                          Déjà noté : {legacyControlNote('sous_sols_controle', key, sousSolsOptions).join(', ')}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mb-2">
                        {renderRasButton({
                          selected: isOptionSelected('sous_sols_controle', key, 'RAS'),
                          onClick: () => chooseControlOption('sous_sols_controle', key, 'RAS'),
                          testId: `sous-sol-ras-${key}`,
                        })}
                        {sousSolsOptions.filter(opt => opt !== 'RAS').map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => chooseControlOption('sous_sols_controle', key, opt)}
                            className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${
                              isOptionSelected('sous_sols_controle', key, opt)
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      {selectedCategories('sous_sols_controle', key).filter(cat => sousSolsOptions.includes(cat)).map(cat => (
                        <div key={cat} className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-muted-foreground min-w-[160px]">{cat} :</span>
                          <Input placeholder="Précision (ex: x2)" value={getOptionAnnotation('sous_sols_controle', key, cat)} onChange={(e) => setOptionAnnotation('sous_sols_controle', key, cat, e.target.value)} data-testid={`input-sous-sol-note-${key}-${cat}`} className="text-sm h-8 max-w-[180px]" />
                        </div>
                      ))}
                      <Input placeholder="Remarque (texte libre)..." value={formData.sous_sols_remarks?.[key] || ''} onChange={(e) => handleNestedChange('sous_sols_remarks', key, e.target.value)} data-testid={`input-sous-sol-remark-${key}`} className="text-sm mt-1" />
                      <div className="mt-3 pt-3 border-t border-border/60">
                        <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Horaires de passage</Label>
                        <div className="space-y-2">
                          {(formData.sous_sols_passages?.[key] || []).map((heure, index) => (
                            <div key={index} className="flex gap-2 items-center">
                              <span className="text-xs text-muted-foreground w-6">{index + 1}.</span>
                              <Input
                                type="time"
                                value={heure}
                                onChange={(e) => handlePassageChange('sous_sols_passages', key, index, e.target.value)}
                                data-testid={`input-sous-sol-passage-${key}-${index}`}
                                className="h-8 text-sm max-w-[130px]"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemovePassage('sous_sols_passages', key, index)}
                                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm"
                                aria-label="Retirer cette heure"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleAddPassage('sous_sols_passages', key, nowHHMM())}
                            data-testid={`btn-now-sous-sol-passage-${key}`}
                            className="h-12 px-4 rounded-md bg-primary text-primary-foreground text-sm font-bold"
                          >
                            Maintenant
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddPassage('sous_sols_passages', key)}
                            data-testid={`btn-add-sous-sol-passage-${key}`}
                            className="h-12 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
                          >
                            + Ajouter une heure
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-temperatures" value="temperatures" className={`bg-card border border-border rounded-lg ${stepClass('temperatures')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><Thermometer className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Températures Frigos</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {formData.temperature_checks.map((temp, index) => {
                    const typed = String(temp.temperature || '').trim();
                    const explicitSign = /^[+\-−–]/.test(typed);
                    return (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-zinc-900/50 rounded-lg border border-border">
                      <div><Label>Élément</Label><Input value={temp.location} disabled className="bg-zinc-800" /></div>
                      <div>
                        <Label>Température (°C)</Label>
                        <div className="flex items-center gap-2">
                          {!explicitSign && (
                            <span className="inline-flex h-12 min-w-10 items-center justify-center rounded-md border border-border bg-zinc-800 px-2 text-base font-semibold" aria-hidden="true" data-testid={`temp-sign-${index}`}>
                              {temperaturePolarity(temp.location, index)}
                            </span>
                          )}
                          <Input placeholder="Ex. 4" value={temp.temperature} onChange={(e) => handleTemperatureChange(index, 'temperature', e.target.value)} data-testid={`input-temperature-${index}`} aria-label={`Température ${temp.location || index + 1}`} />
                        </div>
                      </div>
                      <div><Label>Heure</Label><Input type="time" value={temp.time} onChange={(e) => handleTemperatureChange(index, 'time', e.target.value)} aria-label={`Heure ${temp.location || index + 1}`} /></div>
                    </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground">Saisissez le nombre seul. Le rapport ajoute le signe et °C : + pour les deux frigos, − pour les congélateurs. Un signe déjà saisi est conservé.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-manoeuvres" value="manoeuvres" className={`bg-card border border-border rounded-lg ${stepClass('manoeuvres')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Manœuvres à effectuer</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-3">
                  {manoeuvresList.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucune manœuvre configurée. Ajoutez-en via le panneau Admin.</p>
                  ) : (
                    manoeuvresList.map(manoeuvre => (
                      <div key={manoeuvre.id} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                        <Checkbox 
                          id={`manoeuvre-${manoeuvre.id}`} 
                          checked={formData.manoeuvres?.[manoeuvre.name] || false} 
                          onCheckedChange={(checked) => handleNestedChange('manoeuvres', manoeuvre.name, checked)} 
                        />
                        <Label htmlFor={`manoeuvre-${manoeuvre.id}`} className="cursor-pointer flex-1">{manoeuvre.name}</Label>
                        <Input type="time" value={formData.manoeuvre_times?.[manoeuvre.name] || ''} onChange={(e) => handleNestedChange('manoeuvre_times', manoeuvre.name, e.target.value)} className="max-w-[100px] text-sm" />
                      </div>
                    ))
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>


            <AccordionItem id="section-controle-exterieurs" value="controle-exterieurs" className={`bg-card border border-border rounded-lg ${stepClass('controle-exterieurs')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Extérieurs Zones</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <Label className="text-base font-semibold mb-3 block">Heures de passage (Bâtiment - Parking - Alentours)</Label>
                <div className="space-y-2">
                  {formData.controle_exterieurs.map((heure, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
                      <Input
                        type="time"
                        value={heure}
                        onChange={(e) => handleControleHeureChange('controle_exterieurs', index, e.target.value)}
                        className="flex-1 max-w-xs"
                      />
                      {formData.controle_exterieurs.length > 1 && (
                        <button
                          onClick={() => handleRemoveControleHeure('controle_exterieurs', index)}
                          className="px-3 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleAddControleHeure('controle_exterieurs', nowHHMM())}
                    data-testid="btn-now-exterieurs"
                    className="h-12 px-4 rounded-md bg-primary text-primary-foreground text-sm font-bold"
                  >
                    Maintenant
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddControleHeure('controle_exterieurs')}
                    className="h-12 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
                  >
                    + Ajouter une heure
                  </button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-controle-internes" value="controle-internes" className={`bg-card border border-border rounded-lg ${stepClass('controle-internes')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Contrôle Zones Internes Hôtel</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <Label className="text-base font-semibold mb-3 block">Heures de passage (Étages / du 1er au 5ème)</Label>
                <div className="space-y-2">
                  {formData.controle_internes_hotel.map((heure, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
                      <Input
                        type="time"
                        value={heure}
                        onChange={(e) => handleControleHeureChange('controle_internes_hotel', index, e.target.value)}
                        className="flex-1 max-w-xs"
                      />
                      {formData.controle_internes_hotel.length > 1 && (
                        <button
                          onClick={() => handleRemoveControleHeure('controle_internes_hotel', index)}
                          className="px-3 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleAddControleHeure('controle_internes_hotel', nowHHMM())}
                    data-testid="btn-now-internes"
                    className="h-12 px-4 rounded-md bg-primary text-primary-foreground text-sm font-bold"
                  >
                    Maintenant
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddControleHeure('controle_internes_hotel')}
                    className="h-12 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
                  >
                    + Ajouter une heure
                  </button>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-observations-etages" value="observations-etages" className={`bg-card border border-border rounded-lg ${stepClass('observations-etages')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Observations par Étages</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  {Object.keys(formData.observations_etages || {}).map(etage => {
                    const options = observationsEtagesOptions[etage];
                    
                    if (options) {
                      // Étages avec boutons d'options
                      return (
                        <div key={etage} className="p-4 bg-zinc-900/50 rounded-lg border border-border">
                          <Label className="text-base font-semibold mb-2 block">{etage}</Label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {options.includes('RAS') && renderRasButton({
                              selected: isOptionSelected('observations_etages', etage, 'RAS'),
                              onClick: () => chooseControlOption('observations_etages', etage, 'RAS'),
                              testId: `obs-ras-${etage}`,
                            })}
                            {options.filter(opt => opt !== 'RAS').map(opt => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => chooseControlOption('observations_etages', etage, opt)}
                                className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${
                                  isOptionSelected('observations_etages', etage, opt)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'
                                }`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                          {selectedCategories('observations_etages', etage).map(cat => (
                            <div key={cat} className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-muted-foreground min-w-[160px]">{cat} :</span>
                              <Input placeholder="Précision (ex: x2)" value={getOptionAnnotation('observations_etages', etage, cat)} onChange={(e) => setOptionAnnotation('observations_etages', etage, cat, e.target.value)} data-testid={`input-obs-note-${etage}-${cat}`} className="text-sm h-8 max-w-[180px]" />
                            </div>
                          ))}
                          <Input
                            placeholder="Remarque (texte libre)..."
                            value={formData.observations_remarks?.[etage] || ''}
                            onChange={(e) => handleNestedChange('observations_remarks', etage, e.target.value)}
                            data-testid={`input-obs-remark-${etage}`}
                            className="text-sm mt-1"
                          />
                        </div>
                      );
                    } else {
                      // Étage Sous-sol : champ texte libre uniquement
                      return (
                        <div key={etage}>
                          <Label htmlFor={`obs-${etage}`}>{etage}</Label>
                          <Input
                            id={`obs-${etage}`}
                            type="text"
                            placeholder="RAS ou observations..."
                            value={formData.observations_etages?.[etage] || ''}
                            onChange={(e) => handleNestedChange('observations_etages', etage, e.target.value)}
                            data-testid={`input-obs-${etage}`}
                          />
                        </div>
                      );
                    }
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-incidents" value="incidents" className={`bg-card border border-border rounded-lg ${stepClass('incidents')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><AlertTriangle className="w-5 h-5 text-destructive" /><span className="text-xl font-bold uppercase">Remarques / Incidents</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                {remarkGroups.length > 0 && (
                  <div className="mb-4 rounded-lg border border-border bg-zinc-900/40">
                    <button
                      type="button"
                      onClick={() => setShowRemarkTemplates(v => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                      data-testid="toggle-remark-templates"
                    >
                      <span className="text-sm font-bold uppercase tracking-wide text-foreground">
                        Remarques fréquentes
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {showRemarkTemplates ? 'Masquer ▲' : 'Afficher ▼'}
                      </span>
                    </button>
                    {showRemarkTemplates && (
                      <div className="px-4 pb-4 space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Un clic ajoute la remarque avec l&apos;heure actuelle. Complétez ensuite
                          les points de suspension (nom, numéro de chambre, heure).
                        </p>
                        {remarkGroups.map(([category, list]) => (
                          <div key={category}>
                            <div className="text-xs uppercase text-muted-foreground mb-1">{category}</div>
                            <div className="flex flex-wrap gap-2">
                              {list.map(text => {
                                const label = resolveTemplate(text);
                                return (
                                  <button
                                    key={text}
                                    type="button"
                                    title={label}
                                    onClick={() => handleAddIncidentFromTemplate(text)}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-800 text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors text-left"
                                  >
                                    + {label.length > 58 ? `${label.slice(0, 58)}…` : label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-3">
                  {formData.incidents.map((incident, index) => (
                    <div
                      key={incident.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className="p-4 bg-zinc-900/50 rounded-lg border border-border cursor-move hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <div className="text-2xl text-muted-foreground cursor-move">⋮⋮</div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                          <div>
                            <Label className="text-xs">Heure (HH:MM)</Label>
                            <Input
                              type="time"
                              value={incident.time}
                              onChange={(e) => handleIncidentChange(incident.id, 'time', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div className="md:col-span-4">
                            <Label className="text-xs">Description</Label>
                            <Textarea
                              placeholder="Remise des clés 502 à un client..."
                              value={incident.description}
                              onChange={(e) => handleIncidentChange(incident.id, 'description', e.target.value)}
                              className="mt-1 min-h-[60px] resize-y"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveIncident(incident.id)}
                          className="px-3 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddIncident}
                  className="mt-4 w-full h-11 px-4 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-colors"
                >
                  + Ajouter une remarque/incident
                </button>
                <p className="text-xs text-muted-foreground mt-3">
                  💡 Format: HH:MM / Description (ex: 23:52 / Remise des clés 502 à un client)<br/>
                  🔄 Glissez-déposez pour réorganiser l'ordre
                </p>
                <div className="mt-5 space-y-3" data-testid="report-photos">
                  <p className="text-sm font-semibold">Photos d'une anomalie</p>
                  <p className="text-xs text-muted-foreground">
                    Chaque photo est liée à ce rapport. Au transfert, elle est envoyée avant de passer la main. Hors réseau, le transfert attend.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <label className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary text-primary-foreground font-semibold cursor-pointer">
                      <Camera className="w-4 h-4" />
                      Appareil photo
                      <input data-testid="input-report-camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={handleAddReportPhoto} />
                    </label>
                    <label className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-secondary text-secondary-foreground font-semibold cursor-pointer">
                      Choisir une photo
                      <input data-testid="input-report-file" type="file" accept="image/*" className="hidden" onChange={handleAddReportPhoto} />
                    </label>
                  </div>
                  {serverPhotos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="server-report-photos">
                      {serverPhotos.map((photo) => {
                        const decision = sentPhotoActions(canWriteReport);
                        return (
                        <div key={photo.id} className="rounded-md overflow-hidden border border-border bg-zinc-900">
                          <img src={photo.url} alt="Photo du rapport" className="w-full h-28 object-cover" />
                          {decision.canDelete && (
                            <button
                              type="button"
                              data-testid="btn-delete-sent-photo"
                              onClick={() => handleDeleteSentPhoto(photo)}
                              className="w-full h-11 text-sm font-semibold text-primary-foreground bg-primary"
                            >
                              {decision.label}
                            </button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                  {localPhotos.filter((photo) => !formData.id || !photo.reportId || photo.reportId === formData.id).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {localPhotos.filter((photo) => !formData.id || !photo.reportId || photo.reportId === formData.id).map((photo) => (
                        <div key={photo.id} className="relative rounded-md overflow-hidden border border-border bg-zinc-900">
                          {photo.url ? <img src={photo.url} alt="" className="w-full h-28 object-cover" /> : null}
                          <button type="button" onClick={() => handleRemoveReportPhoto(photo.id)} className="absolute top-1 right-1 h-8 px-2 rounded-md bg-background/80 text-xs font-semibold">
                            Retirer
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-remise-materiel" value="remise-materiel" className={`bg-card border border-border rounded-lg ${stepClass('remise-materiel')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><Package className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Remise Matériel au Personnel Réception</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Personnel du matin (Mme/M.)</Label>
                      <div className="space-y-2 mt-1">
                        <div className="flex flex-wrap gap-2">
                          {receptionnistesList.map(name => (
                            <button key={name} type="button" onClick={() => handleInputChange('remise_materiel_personnel', name)}
                              className={`inline-flex min-h-11 items-center px-3 rounded-md text-sm font-medium transition-colors ${formData.remise_materiel_personnel === name ? 'bg-primary text-primary-foreground' : 'bg-zinc-800 text-muted-foreground hover:bg-zinc-700'}`}>{name}</button>
                          ))}
                        </div>
                        <Input placeholder="Ou entrer un nom manuellement..." value={formData.remise_materiel_personnel} onChange={(e) => handleInputChange('remise_materiel_personnel', e.target.value)} data-testid="input-remise-personnel" aria-label="Personnel de la remise" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="remise-heure">Heure d'arrivée du personnel</Label>
                      <Input 
                        id="remise-heure"
                        type="time"
                        value={formData.remise_materiel_heure}
                        onChange={(e) => handleInputChange('remise_materiel_heure', e.target.value)}
                        data-testid="input-remise-heure"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="remise-heure-agent">Heure de remise par l'agent</Label>
                      <Input 
                        id="remise-heure-agent"
                        type="time"
                        value={formData.remise_materiel_heure_agent}
                        onChange={(e) => handleInputChange('remise_materiel_heure_agent', e.target.value)}
                        data-testid="input-remise-heure-agent"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="remise-materiel">Détails / Remarques sur la remise</Label>
                    <Textarea 
                      id="remise-materiel"
                      placeholder="Détails supplémentaires sur la remise de matériel..."
                      value={formData.remise_materiel_matin}
                      onChange={(e) => handleInputChange('remise_materiel_matin', e.target.value)}
                      data-testid="textarea-remise-materiel"
                      className="min-h-[80px] mt-1"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem id="section-materiel" value="materiel" className={`bg-card border border-border rounded-lg ${stepClass('materiel')}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-3"><Package className="w-5 h-5 text-primary" /><span className="text-xl font-bold uppercase">Matériel Remis/Déposé</span></div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4">
                <div className="space-y-3">
                  {Object.keys(formData.material_checklist || {}).map(item => (
                    <div key={item} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                      <Checkbox id={`material-${item}`} checked={formData.material_checklist?.[item] || false} onCheckedChange={(checked) => handleNestedChange('material_checklist', item, checked)} />
                      <Label htmlFor={`material-${item}`} className="cursor-pointer flex-1">{item}</Label>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {isMobile && mobileStep < REPORT_STEPS.length - 1 && (
            <button type="button" onClick={() => setMobileStep((step) => step + 1)} className="h-14 w-full rounded-lg bg-primary text-lg font-bold text-primary-foreground">
              Suivant — {REPORT_STEPS[mobileStep + 1].label}
            </button>
          )}
          <div id="section-finaliser" className={`flex flex-col md:flex-row gap-4 pt-6 ${stepClass('finaliser')}`}>
            <button onClick={() => navigate(editMode ? '/historique' : '/dashboard')} data-testid="btn-cancel" className="flex-1 h-11 px-8 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-colors">Annuler</button>
            {!editMode && (
              <button onClick={() => saveDraft({ silent: false })} disabled={draftSaving} data-testid="btn-save-draft-bottom" className="flex-1 h-11 px-8 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-colors disabled:opacity-50">
                {draftSaving ? 'Sauvegarde du brouillon...' : 'Sauvegarder le brouillon'}
              </button>
            )}
            {!editMode && (
              <DeleteDraftButton
                agentId={currentAgent?.id}
                reportId={formData.id || ''}
                onBeforeDelete={() => { abandonLockedRef.current = true; }}
                onFailed={() => { abandonLockedRef.current = false; }}
                onDeleted={() => {
                  setFormTouched(false);
                  navigate('/dashboard');
                }}
                className="flex-1 h-11 px-8 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold transition-colors"
              />
            )}
            <button onClick={handleSubmit} disabled={loading} data-testid="btn-submit-report" className="flex-1 h-11 px-8 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold uppercase tracking-wide shadow-[0_0_15px_rgba(37,99,235,0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 active:scale-95">
              {loading ? <span className="flex items-center justify-center gap-2"><Save className="w-4 h-4 animate-spin" />Enregistrement...</span> : <span className="flex items-center justify-center gap-2"><Save className="w-4 h-4" />{editMode ? 'Modifier le rapport' : 'Enregistrer le rapport'}</span>}
            </button>
          </div>
          <Accordion type="single" collapsible className={`mt-4 ${stepClass('finaliser')}`}>
            <AccordionItem value="transfer" className="rounded-lg border border-primary/20 bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <span className="text-sm font-bold uppercase tracking-wide text-foreground">Transférer le rapport</span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="transfer-agent">Agent qui reprend</Label>
                    <select
                      id="transfer-agent"
                      value={transferAgentId}
                      onChange={(e) => setTransferAgentId(e.target.value)}
                      data-testid="select-transfer-agent"
                      className="flex h-11 w-full rounded-md border border-input bg-card px-3 py-1 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option className="bg-card text-foreground" value="">Choisir un agent...</option>
                      {agentsList
                        .filter(agent => agent.id !== currentAgent?.id)
                        .map(agent => (
                          <option className="bg-card text-foreground" key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="transfer-time">Heure du transfert</Label>
                    <Input
                      id="transfer-time"
                      type="time"
                      value={formData.transfer_time || ''}
                      onChange={(e) => handleInputChange('transfer_time', e.target.value)}
                      data-testid="input-transfer-time"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleTransferReport}
                      disabled={transferSaving}
                      data-testid="btn-transfer-report"
                      className="w-full h-11 px-4 rounded-md bg-primary/10 hover:bg-primary/20 text-primary font-semibold transition-colors disabled:opacity-50"
                    >
                      {transferSaving ? 'Transfert...' : 'Transférer'}
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  <Label htmlFor="transfer-notes">Notes de transfert (optionnel)</Label>
                  <Textarea
                    id="transfer-notes"
                    value={formData.transfer_notes || ''}
                    onChange={(e) => handleInputChange('transfer_notes', e.target.value)}
                    placeholder="Ex: consignes particulières pour l'agent qui reprend..."
                    data-testid="textarea-transfer-notes"
                    rows={2}
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Le rapport passe à l'agent choisi, dans son état actuel. Vous restez connecté avec votre compte.
                  Le rapport disparaît de vos brouillons et vous ne pouvez plus le modifier. L'autre agent le retrouve à sa prochaine ouverture.
                  Les photos encore sur le téléphone partent avant le transfert.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default ReportForm;
